'use client';
import { useTranslation } from 'next-i18next';
import { IProduct } from '@mybestbrands/types';
import styles from '@/app/ui/components/product/styles.module.scss';
import React, { useState } from 'react';
import CouponOverlay from '@/app/ui/components/list/listitem/couponoverlay';
import VisibilityTracker from '@/app/ui/components/tracking/visibilityTracker';
import { mapProductsToEcommerceItems, trackSSTEvent } from '@/app/utils/tracking';
import CouponCountDownLabel from '@/app/ui/components/list/listitem/couponcountdownlabel';
import { FeatureToggles } from '@/app/featuretoggles/ft';

const CouponLabel = ({ product, locale }: { product: IProduct; locale: string }) => {
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);

  const { t } = useTranslation('common');
  const { discountCoupon, newCustomerCoupon, freeShippingCoupon, originalPrice, price, priorityCoupon } = product;
  const hasDiscount = discountCoupon && originalPrice !== price; // discount coupon + sale

  const gaTrackingData = {
    currency: 'EUR',
    value: product.epc / 100,
    items: mapProductsToEcommerceItems([product]),
  };

  const getCouponTitle = () => {
    if (discountCoupon) {
      if (hasDiscount) return `Extra-${t('COUPON')}`;
      else return t('COUPON');
    }
    if (freeShippingCoupon) {
      return t('SHIPPING_COUPON');
    }
    if (newCustomerCoupon) {
      return t('NEW_CUSTOMER_COUPON');
    }
    return;
  };

  const getCouponAmountText = () => {
    if (priorityCoupon?.amount) {
      const amount = priorityCoupon.amount;
      const amountType = priorityCoupon.amount_type;
      return amountType === '%' ? `-${amount}%` : `-${amount?.centsToEuros()}€`;
    }
    return '';
  };

  const couponTitle = getCouponTitle();
  const couponAmountText = getCouponAmountText();

  const handleOverlay = () => {
    setIsOverlayOpen(!isOverlayOpen);
    trackSSTEvent('pdp_gutschein_click', gaTrackingData);
  };

  return (
    <>
      <div className={`cursor-pointer ${styles['couponContainer']}`} onClick={handleOverlay}>
        <span className={`${styles[hasDiscount ? 'extra' : 'regular']} ${styles['coupon']} ${styles['expandedLabel']}`}>
          {couponAmountText ? `${couponAmountText} ${couponTitle}` : couponTitle}
        </span>
        <VisibilityTracker eventName={'pdp_gutschein_display'} data={gaTrackingData} />
        {isOverlayOpen && (
          <CouponOverlay onClose={() => setIsOverlayOpen(false)} item={product} locale={locale} isDetail={true} />
        )}
      </div>
      {!!priorityCoupon?.end_date_utc && (
        <CouponCountDownLabel endTime={priorityCoupon.end_date_utc} isDetail={true} trackingData={gaTrackingData} />
      )}
    </>
  );
};

export default CouponLabel;
